import {
  ComponentCmsDataFormQchoice,
  ComponentCmsDataFormQuestion,
  Enum_Componentcmsdataformqchoice_Jump_Condition_Operation,
  Enum_Componentcmsdataformquestion_Type,
} from "@/generated";
import clsx from "clsx";
import { ChoiceButton } from "./ChoiceButton";
import { OtherOptionButton } from "./OtherOptionButton";
import { useState, useEffect, useCallback, useRef } from "react";
import { Navigation } from "./Navigation";
import { useProcessFormQuestionMutation } from "@/generated";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

interface ChoicesGridProps {
  question: ComponentCmsDataFormQuestion;
  onChoiceSelect: (
    choice: ComponentCmsDataFormQchoice | ComponentCmsDataFormQchoice[]
  ) => void;
  handleOtherSubmit: (otherAnswer: string) => void;
  onPrevious: () => void;
  onNext: () => void;
  canGoBack: boolean;
  canGoForward: boolean;
  isMobile?: boolean;
  resetForm?: () => void;
}

export const ChoicesGrid: React.FC<ChoicesGridProps> = ({
  question,
  onChoiceSelect,
  handleOtherSubmit,
  onPrevious,
  onNext,
  canGoBack,
  canGoForward,
  isMobile = false,
  resetForm,
}) => {
  const [selectedChoices, setSelectedChoices] = useState<
    ComponentCmsDataFormQchoice[]
  >([]);
  const [inputValue, setInputValue] = useState("");
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const { t: tCommon } = useTranslation("common");

  // Add the processFormQuestion mutation
  const [processFormQuestionMutation] = useProcessFormQuestionMutation();

  const [SelectDatepickerComponent, setSelectDatepickerComponent] =
    useState<any>(null);

  useEffect(() => {
    let isMounted = true;

    const loadDatepicker = async () => {
      try {
        // Use dynamic import with a specific export
        const datepickerModule = await import("react-select-datepicker");
        if (isMounted) {
          setSelectDatepickerComponent(() => datepickerModule.SelectDatepicker);
        }
      } catch (error) {
        console.error("Failed to load datepicker:", error);
      }
    };

    loadDatepicker();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSubmit = useCallback(() => {
    if (isOtherSelected) {
      handleOtherSubmit(inputValue);
      onNext();
      return;
    }

    // For Calculate type, process the RFID
    if (question.type === "Calculate" && inputValue && !isProcessing) {
      setIsProcessing(true);

      processFormQuestionMutation({
        onCompleted: (response) => {
          const nextQuestionIndex = response.processFormQuestion;

          if (nextQuestionIndex) {
            // Clear the input and processing state
            setInputValue("");
            setIsProcessing(false);

            // Create a mock choice to navigate to the next question
            const mockChoice: ComponentCmsDataFormQchoice = {
              id: `calculate_${Date.now()}`,
              choice_text: inputValue,
              jump_to_question_index: nextQuestionIndex,
              jump_condition_operation:
                Enum_Componentcmsdataformqchoice_Jump_Condition_Operation.ResponseIsEqualWithChoice,
              display_delay: 0,
            };

            onChoiceSelect(mockChoice);
          } else {
            if (question.restart_form_timer) {
              // Clear any existing timer
              if (timerRef.current) {
                clearTimeout(timerRef.current);
              }

              // Set new timer to refresh page
              timerRef.current = setTimeout(() => {
                resetForm?.();
              }, question.restart_form_timer * 1000);
            }
          }
        },
        onError: (error) => {
          console.error("Error processing RFID:", error);
          toast.error(tCommon("error-occurred") + ": " + error.message);
          setIsProcessing(false);

          if (question.restart_form_timer) {
            // Clear any existing timer
            if (timerRef.current) {
              clearTimeout(timerRef.current);
            }

            // Set new timer to refresh page
            timerRef.current = setTimeout(() => {
              resetForm?.();
            }, question.restart_form_timer * 1000);
          }
        },
        variables: {
          rfid: inputValue,
          questionId: question.id,
        },
      });
      return;
    }

    switch (question.type) {
      case Enum_Componentcmsdataformquestion_Type.MultipleChoices:
        if (selectedChoices.length > 0) {
          onChoiceSelect(selectedChoices);
        }
        break;

      case Enum_Componentcmsdataformquestion_Type.SingleChoice:
        const selectedChoice = selectedChoices[0];
        if (selectedChoice) {
          onChoiceSelect(selectedChoice);
        }
        break;

      case Enum_Componentcmsdataformquestion_Type.Number:
      case Enum_Componentcmsdataformquestion_Type.Date:
      case Enum_Componentcmsdataformquestion_Type.Time:
      case Enum_Componentcmsdataformquestion_Type.DateAndTime:
      case Enum_Componentcmsdataformquestion_Type.Birthday:
        const matchingChoice = question.choices?.find((choice) => {
          if (!choice?.choice_text) return false;
          const inputDate = new Date(inputValue);
          const choiceDate = new Date(choice.choice_text);

          switch (choice.jump_condition_operation) {
            case "response_is_greater_than_choice":
              return inputDate > choiceDate;
            case "response_is_less_than_choice":
              return inputDate < choiceDate;
            case "response_is_equal_with_choice":
              return inputDate.getTime() === choiceDate.getTime();
            default:
              return false;
          }
        });
        if (matchingChoice) {
          onChoiceSelect(matchingChoice);
        } else {
          handleOtherSubmit(inputValue);
        }
        break;

      case Enum_Componentcmsdataformquestion_Type.Rating:
        const ratingValue = parseInt(inputValue, 10);
        const matchingRatingChoice = question.choices?.find((choice) => {
          if (!choice?.choice_text) return false;
          const choiceValue = parseInt(choice.choice_text, 10);

          switch (choice.jump_condition_operation) {
            case "response_is_greater_than_choice":
              return ratingValue > choiceValue;
            case "response_is_less_than_choice":
              return ratingValue < choiceValue;
            case "response_is_equal_with_choice":
              return ratingValue === choiceValue;
            default:
              return false;
          }
        });

        if (matchingRatingChoice) {
          onChoiceSelect(matchingRatingChoice);
        } else {
          handleOtherSubmit(inputValue);
        }
        break;

      case Enum_Componentcmsdataformquestion_Type.Email:
      case Enum_Componentcmsdataformquestion_Type.FirstName:
      case Enum_Componentcmsdataformquestion_Type.LastName:
      case Enum_Componentcmsdataformquestion_Type.PhoneNumber:
      case Enum_Componentcmsdataformquestion_Type.ShortText:
      case Enum_Componentcmsdataformquestion_Type.LongText:
      default:
        const textChoice = question.choices?.find((choice) => {
          if (!choice?.choice_text) return false;
          const normalizedInput = inputValue.toLowerCase().trim();
          const normalizedChoice = choice.choice_text.toLowerCase().trim();

          switch (choice.jump_condition_operation) {
            case "response_is_equal_with_choice":
              return normalizedInput === normalizedChoice;
            case "response_is_not_equal_with_choice":
              return normalizedInput !== normalizedChoice;
            default:
              return false;
          }
        });

        if (textChoice) {
          onChoiceSelect(textChoice);
        } else {
          handleOtherSubmit(inputValue);
        }
    }

    onNext();
  }, [
    isOtherSelected,
    question.type,
    question.id,
    question.restart_form_timer,
    question.choices,
    inputValue,
    isProcessing,
    onNext,
    handleOtherSubmit,
    processFormQuestionMutation,
    onChoiceSelect,
    tCommon,
    selectedChoices,
    resetForm,
  ]);

  const canSubmit =
    (question.type === Enum_Componentcmsdataformquestion_Type.MultipleChoices &&
      selectedChoices.length > 0) ||
    (question.type !== Enum_Componentcmsdataformquestion_Type.MultipleChoices &&
      question.type !== Enum_Componentcmsdataformquestion_Type.SingleChoice &&
      question.type !== "Calculate" &&
      inputValue.length > 0) ||
    (question.type === "Calculate" && inputValue.length > 0 && !isProcessing) ||
    (isOtherSelected && inputValue.length > 0);

  // Add handler for multiple choice selection
  const handleMultipleChoiceSelect = (choice: ComponentCmsDataFormQchoice) => {
    setSelectedChoices((prev) => {
      const isSelected = prev.some((c) => c.id === choice.id);
      if (isSelected) {
        return prev.filter((c) => c.id !== choice.id);
      } else {
        return [...prev, choice];
      }
    });
  };
  const renderQuestionInput = () => {
    switch (question.type) {
      case Enum_Componentcmsdataformquestion_Type.ShortText:
      case Enum_Componentcmsdataformquestion_Type.LongText:
      case Enum_Componentcmsdataformquestion_Type.FirstName:
      case Enum_Componentcmsdataformquestion_Type.LastName:
      case Enum_Componentcmsdataformquestion_Type.PhoneNumber:
        return (
          <input
            type="text"
            className="w-full p-2 border rounded"
            style={{
              backgroundColor: question.submit_button_color || "#13235b",
              color: question.submit_button_text_color || "white",
            }}
            placeholder={question.input_placeholder || ""}
            multiple={
              question.type === Enum_Componentcmsdataformquestion_Type.LongText
            }
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
        );
      case Enum_Componentcmsdataformquestion_Type.Number:
        return (
          <input
            type="number"
            className="w-full p-2 border rounded"
            style={{
              backgroundColor: question.submit_button_color || "#13235b",
              color: question.submit_button_text_color || "white",
            }}
            placeholder={question.input_placeholder || ""}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
        );
      case Enum_Componentcmsdataformquestion_Type.Email:
        return (
          <input
            type="email"
            className="w-full p-2 border rounded"
            style={{
              backgroundColor: question.submit_button_color || "#13235b",
              color: question.submit_button_text_color || "white",
            }}
            placeholder={question.input_placeholder || ""}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
        );
      case Enum_Componentcmsdataformquestion_Type.PhoneNumber:
        return (
          <input
            type="number"
            className="w-full p-2 border rounded"
            style={{
              backgroundColor: question.submit_button_color || "#13235b",
              color: question.submit_button_text_color || "white",
            }}
            placeholder={question.input_placeholder || ""}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
        );
      case Enum_Componentcmsdataformquestion_Type.Date:
      case Enum_Componentcmsdataformquestion_Type.Birthday:
        if (SelectDatepickerComponent && !isMobile) {
          return (
            <div
              className={clsx([
                "flex w-full p-2 border rounded",
                question.center_text && "justify-center",
              ])}
              style={{
                color: question.submit_button_color || "black",
                backgroundColor: "transparent",
              }}
            >
              <SelectDatepickerComponent
                className="flex gap-5"
                selectedDate={inputValue ? new Date(inputValue) : null}
                onDateChange={(date: Date | null) => {
                  if (date) {
                    setInputValue(date.toISOString());
                  }
                }}
                style={{ backgroundColor: "transparent" }}
                order="day/month/year"
                labels={{
                  yearLabel: " ",
                  monthLabel: " ",
                  dayLabel: " ",
                  yearPlaceholder: tCommon("select-year"),
                  monthPlaceholder: tCommon("select-month"),
                  dayPlaceholder: tCommon("select-day"),
                  months: {
                    1: tCommon("january"),
                    2: tCommon("february"),
                    3: tCommon("march"),
                    4: tCommon("april"),
                    5: tCommon("may"),
                    6: tCommon("june"),
                    7: tCommon("july"),
                    8: tCommon("august"),
                    9: tCommon("september"),
                    10: tCommon("october"),
                    11: tCommon("november"),
                    12: tCommon("december"),
                  },
                }}
              />
            </div>
          );
        } else {
          return (
            <input
              type="date"
              className="w-full p-2 border rounded"
              style={{
                backgroundColor: question.submit_button_color || "#13235b",
                color: question.submit_button_text_color || "white",
              }}
              value={inputValue || new Date().toISOString().split("T")[0]}
              placeholder={question.input_placeholder || ""}
              onChange={(e) => setInputValue(e.target.value)}
            />
          );
        }
      case Enum_Componentcmsdataformquestion_Type.Time:
        return (
          <input
            type="time"
            className="w-full p-2 border rounded"
            style={{
              backgroundColor: question.submit_button_color || "#13235b",
              color: question.submit_button_text_color || "white",
            }}
            placeholder={question.input_placeholder || ""}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
        );
      case Enum_Componentcmsdataformquestion_Type.DateAndTime:
        return (
          <input
            type="datetime-local"
            className="w-full p-2 border rounded"
            style={{
              backgroundColor: question.submit_button_color || "#13235b",
              color: question.submit_button_text_color || "white",
            }}
            placeholder={question.input_placeholder || ""}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
        );
      case Enum_Componentcmsdataformquestion_Type.Rating:
        return (
          <div className="flex gap-2 justify-center">
            {[1, 2, 3, 4, 5].map((rating) => (
              <button
                key={rating}
                className="p-1 text-3xl hover:scale-105 transition-transform relative"
                style={{
                  color:
                    rating <= Number(inputValue)
                      ? question.submit_button_color || "#fdd835"
                      : "#e0e0e0",
                }}
                onClick={() => setInputValue(rating.toString())}
              >
                <span className="absolute inset-0 flex items-center justify-center">
                  ★
                </span>
                <span className="relative z-10">
                  {rating <= Number(inputValue) ? "★" : "☆"}
                </span>
              </button>
            ))}
          </div>
        );
      case Enum_Componentcmsdataformquestion_Type.SingleChoice:
        return (
          <div
            className={clsx([
              "grid gap-4",
              isMobile &&
                question.choices?.[0]?.choice_media?.data?.attributes?.url &&
                "grid-cols-2",
              !isMobile &&
                question.nr_columns &&
                `grid-cols-${question.nr_columns || 1}`,
              question.center_text && "justify-items-center",
            ])}
          >
            {question.choices
              ?.filter((c): c is ComponentCmsDataFormQchoice => c !== null)
              .map((choice: ComponentCmsDataFormQchoice, idx: number) => (
                <ChoiceButton
                  key={idx}
                  choice={choice}
                  multiple={false}
                  onSelect={onChoiceSelect}
                  isMobile={isMobile}
                  isOtherSelected={isOtherSelected}
                  size={question.size || "medium"}
                />
              ))}
            {question.allow_other_choice && (
              <OtherOptionButton
                question={question}
                setIsOtherSelected={setIsOtherSelected}
                setInputValue={setInputValue}
                isTransparent={question.is_transparent_button || false}
              />
            )}
          </div>
        );
      case Enum_Componentcmsdataformquestion_Type.MultipleChoices:
        return (
          <>
            <div
              className={clsx([
                "grid gap-4",
                isMobile &&
                  question.choices?.[0]?.choice_media?.data?.attributes?.url &&
                  "grid-cols-2",
                !isMobile &&
                  question.nr_columns &&
                  `grid-cols-${question.nr_columns || 1}`,
                question.center_text && "justify-items-center",
              ])}
            >
              {question.choices
                ?.filter((c): c is ComponentCmsDataFormQchoice => c !== null)
                .map((choice: ComponentCmsDataFormQchoice, idx: number) => (
                  <ChoiceButton
                    key={idx}
                    choice={choice}
                    onSelect={handleMultipleChoiceSelect}
                    multiple={true}
                    isSelected={selectedChoices.some((c) => c.id === choice.id)}
                    isMobile={isMobile}
                    isOtherSelected={isOtherSelected}
                    size={question.size || "medium"}
                  />
                ))}
              {question.allow_other_choice && (
                <OtherOptionButton
                  question={question}
                  setIsOtherSelected={setIsOtherSelected}
                  setInputValue={setInputValue}
                  isTransparent={question.is_transparent_button || false}
                />
              )}
            </div>
          </>
        );
      case Enum_Componentcmsdataformquestion_Type.Calculate:
        return (
          <div className="w-full flex flex-col gap-4">
            <input
              type="text"
              className="w-full p-4 text-xl border rounded-lg"
              style={{
                backgroundColor: question.submit_button_color || "#13235b",
                color: question.submit_button_text_color || "white",
              }}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={question.input_placeholder || "Scan RFID Card"}
              autoFocus
              disabled={isProcessing}
            />
            {isProcessing && (
              <div className="text-center text-lg mt-2">
                Processing... Please wait
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // When pressing enter key, submit the current question
      if (!isMobile && event.key === "Enter") {
        handleSubmit();
      }
      // if (!isMobile && event.key === "Backspace") {
      //   onPrevious();
      // }
    };

    if (!isMobile) {
      window.addEventListener("keydown", handleKeyPress);
    }

    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [handleSubmit, isMobile, onPrevious]);

  // Add a watch effect for the RFID input value
  useEffect(() => {
    if (question.type === "Calculate" && inputValue && !isProcessing) {
      // Remove the automatic mutation call - we'll do it only on submit
    }
  }, [
    inputValue,
    question.type,
    question.id,
    isProcessing,
    question.restart_form_timer,
    onChoiceSelect,
    processFormQuestionMutation,
  ]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // Initialize the inactivity timer when the component mounts or when the question changes
  useEffect(() => {
    // Only set up the timer if restart_form_timer is defined and resetForm function exists
    if (question.restart_form_timer && resetForm) {
      // Clear any existing timer first
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // Set a new timer
      timerRef.current = setTimeout(() => {
        resetForm();
      }, question.restart_form_timer * 1000);

      // Set up event listeners to reset the timer on user activity
      const resetInactivityTimer = () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }

        timerRef.current = setTimeout(() => {
          resetForm();
        }, question.restart_form_timer * 1000);
      };

      // Add event listeners for common user interactions
      window.addEventListener("mousemove", resetInactivityTimer);
      window.addEventListener("mousedown", resetInactivityTimer);
      window.addEventListener("keypress", resetInactivityTimer);
      window.addEventListener("touchstart", resetInactivityTimer);
      window.addEventListener("scroll", resetInactivityTimer);

      // Clean up event listeners when component unmounts or question changes
      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }

        window.removeEventListener("mousemove", resetInactivityTimer);
        window.removeEventListener("mousedown", resetInactivityTimer);
        window.removeEventListener("keypress", resetInactivityTimer);
        window.removeEventListener("touchstart", resetInactivityTimer);
        window.removeEventListener("scroll", resetInactivityTimer);
      };
    }
  }, [question.restart_form_timer, resetForm]);

  return (
    <>
      {renderQuestionInput()}
      {question.type === Enum_Componentcmsdataformquestion_Type.SingleChoice &&
      !question.allow_other_choice ? (
        ""
      ) : (
        <Navigation
          onPrevious={onPrevious}
          onNext={onNext}
          onSubmit={handleSubmit}
          canGoBack={canGoBack}
          canGoForward={canGoForward}
          canSubmit={canSubmit}
          submitButtonColor={question.submit_button_color || "#13235b"}
          submitButtonTextColor={question.submit_button_text_color || "white"}
          submitButtonText={question.submit_button_text || "OK"}
          submitButtonSticky={question.submit_button_sticky || false}
          submitButtonStickyBackgroundColor={
            question.background_color || "#13235b"
          }
          isTransparent={question.is_transparent_button || false}
        />
      )}
    </>
  );
};
